import { useContext, useRef, useState } from 'react'

import { ActionIcon, Menu, Textarea } from '@mantine/core'
import { Check, X } from 'lucide-react'
import { ActivityLog } from 'types/graphql'

import { useMutation } from '@redwoodjs/web'

import ActorBadge from 'src/components/ActivityLogs/shared/ActorBadge'
import DNButton from 'src/components/Buttons/Button/DNButton'
import MentionText from 'src/components/DataDisplay/MentionText/MentionText'
import MentionHighlightBackdrop from 'src/components/Inputs/MentionHighlightBackdrop/MentionHighlightBackdrop'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { RecruitersContext } from 'src/context/RecruitersContext'
import { useTaskModalContext } from 'src/context/TaskModalContext'
import {
  UPDATE_ACTIVITY_LOG,
  DELETE_ACTIVITY_LOG,
} from 'src/graphql/activityLogs.graphql'
import IconEdit from 'src/icons/IconEdit'
import IconThreeDots from 'src/icons/IconThreeDots'
import IconTrash from 'src/icons/IconTrash'
import { describeFollowUp } from 'src/lib/followUpStatus'
import { formatDate } from 'src/lib/formatters'
import { buildMentionTags } from 'src/lib/mentions.utils'
import {
  replaceNamesWithRecruitersId,
  replaceRecruitersIdWithName,
} from 'src/lib/recruiters.utils'

interface NoteLogCardProps {
  activityLog: ActivityLog
  refetchQueries?: any[]
}

const NoteLog = ({ activityLog, refetchQueries = [] }: NoteLogCardProps) => {
  const recruiters = useContext(RecruitersContext)
  // Null outside the dashboard layout (tests, stories).
  const taskModal = useTaskModalContext()
  const followUp = activityLog.task ? describeFollowUp(activityLog.task) : null
  const [isEditing, setIsEditing] = useState(false)
  // Edited by name: stored `@{{employee:id}}` tags are shown as `@First Last`
  // and converted back on save, like the composer does.
  const [editedContent, setEditedContent] = useState('')
  const editInputRef = useRef<HTMLTextAreaElement>(null)
  const editableContent = () =>
    replaceRecruitersIdWithName(activityLog.content, recruiters ?? [])
  const [deleteModalOpened, setDeleteModalOpened] = useState(false)

  const [updateActivityLog, { loading: updating }] = useMutation(
    UPDATE_ACTIVITY_LOG,
    {
      onCompleted: () => {
        toast('Note updated successfully', 'success')
        setIsEditing(false)
      },
      onError: () => {
        toast('Failed to update note', 'error')
      },
      refetchQueries,
    }
  )

  const [deleteActivityLog, { loading: deleting }] = useMutation(
    DELETE_ACTIVITY_LOG,
    {
      onCompleted: () => {
        toast('Note deleted successfully', 'success')
        setDeleteModalOpened(false)
      },
      onError: () => {
        toast('Failed to delete note', 'error')
      },
      refetchQueries,
    }
  )

  const handleSave = async () => {
    if (editedContent.trim() === '') {
      toast('Note cannot be empty', 'error')
      return
    }

    const content = replaceNamesWithRecruitersId(
      editedContent,
      recruiters ?? []
    )
    await updateActivityLog({
      variables: {
        id: activityLog.activityLogId,
        input: {
          content,
          tags: buildMentionTags(content),
        },
      },
    })
  }

  const handleEdit = () => {
    setEditedContent(editableContent())
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  const handleDelete = async () => {
    await deleteActivityLog({
      variables: {
        id: activityLog.activityLogId,
      },
    })
  }

  return (
    <>
      <div
        className={`flex flex-col-reverse items-end gap-2 border-doubleNickel-gray-300 p-1 py-4`}
      >
        {/* First in DOM = bottom of the reversed column, i.e. under the text. */}
        {followUp && !isEditing && (
          <div className="flex w-full flex-row justify-end text-xs">
            <button
              type="button"
              className={`font-medium hover:underline ${followUp.className}`}
              onClick={() =>
                taskModal?.handleOpen?.({ id: activityLog.task.taskId })
              }
            >
              {followUp.label}
            </button>
          </div>
        )}
        <div className="flex w-full flex-row items-start gap-2">
          {isEditing ? (
            <div className="flex flex-1 flex-col gap-2">
              <div className="relative">
                <MentionHighlightBackdrop
                  content={editedContent}
                  recruiters={recruiters}
                  textareaRef={editInputRef}
                />
                <Textarea
                  ref={editInputRef}
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.currentTarget.value)}
                  minRows={3}
                  autosize
                  disabled={updating}
                  className="relative z-10"
                  classNames={{ input: '!bg-transparent' }}
                />
              </div>
              <div className="flex justify-end gap-2">
                <DNButton
                  variant="transparent"
                  onClick={handleSave}
                  disabled={updating}
                  className="text-doubleNickel-success-500"
                >
                  <Check size={14} />
                  Save
                </DNButton>
                <DNButton
                  variant="transparent"
                  onClick={handleCancel}
                  disabled={updating}
                  className="text-doubleNickel-error-500"
                >
                  <X size={14} />
                  Cancel
                </DNButton>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 hyphens-auto whitespace-pre-line text-sm text-doubleNickel-gray-900 [overflow-wrap:anywhere]">
                <MentionText
                  content={activityLog.content}
                  recruiters={recruiters}
                />
              </div>
              <Menu shadow="md" width={150} position="bottom-end">
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label="Note actions"
                  >
                    <IconThreeDots className="h-4 w-4" />
                  </ActionIcon>
                </Menu.Target>

                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={
                      <IconEdit className="h-3.5 w-3.5 fill-none stroke-current" />
                    }
                    onClick={handleEdit}
                  >
                    Edit
                  </Menu.Item>
                  <Menu.Item
                    leftSection={
                      <IconTrash className="h-3.5 w-3.5 fill-none stroke-current" />
                    }
                    onClick={() => setDeleteModalOpened(true)}
                  >
                    Delete
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </>
          )}
        </div>
        <div className={`flex flex-1 flex-row gap-2 text-xs`}>
          {activityLog.employee ? (
            <div className="font-medium text-doubleNickel-gray-600">
              {`${activityLog.employee.firstName} ${activityLog.employee.lastName}`}{' '}
            </div>
          ) : (
            // Notes without an author were written by the system (e.g. an AI
            // recruiter call outcome), not by a recruiter.
            <ActorBadge variant="auto" label="Added automatically" />
          )}
          <div className="text-doubleNickel-gray-400">
            {formatDate(activityLog.createdAt)}
          </div>
        </div>
      </div>

      <DeleteModal
        title="Delete Note"
        description="Are you sure you want to delete this note? This action cannot be undone."
        opened={deleteModalOpened}
        close={() => setDeleteModalOpened(false)}
        onDelete={handleDelete}
      />
    </>
  )
}

export default NoteLog
